import { useEffect, useMemo, useRef, useState } from "react";
import { PictureOutlined, UserOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { Avatar, Badge, Button, Card, Drawer, message, Space, Table, Tag, Tooltip, type BadgeProps } from "antd";
import dayjs from "dayjs";
import keyBy from "lodash.keyby";
import round from "lodash.round";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { nutritionActions } from "@fitness-app/app-store/src/store/reducers/nutrition";
import { PermissionKey, PermissionScope } from "@fitness-app/data-models";
import { type Dish } from "@fitness-app/data-models/entities/Dish";
import {
  diffIngredientCorrection,
  DishIssueSource,
  DishIssueStatus,
  IngredientIssueOutcome,
} from "@fitness-app/data-models/entities/DishIssue";
import { getName } from "@fitness-app/data-models/utils/getAuthorName";
import { getUserInitials } from "@fitness-app/utils";
import { calculateMeasureMultiplier } from "@fitness-app/utils/src/nutrition/calculateMeasureMultiplier";
import { recalculateMeasures } from "@fitness-app/utils/src/nutrition/recalculateMeasures";

import ModalForm from "~/components/ModalForm/ModalForm";
import { usePermission } from "~/hooks/usePermission";
import { useUserRole } from "~/hooks/trainer/useUserRole";
import { useEntityChange } from "~/hooks/useEntityChange";
import { useFormHandler } from "~/hooks/useFormHandler";
import { useBackgroundTasks as useBackgroundTasksDrawer } from "~/modules/Dashboard/BackgroundTasksDrawer/BackgroundTasksProvider";
import { useBackgroundTasksQuery } from "~/modules/Dashboard/BackgroundTasksDrawer/useBackgroundTasks";
import { useTeamMembers } from "~/modules/Team/hooks/useTeamMembers";
import { EMPTY_TRAINER_FIELD } from "~/modules/Trainee/helpers/createTrainerOptions";
import { analyticsService, useAppDispatch } from "~/store/initializeStore";
import AddIngredientForm from "../AddIngredientForm/AddIngredientForm";
import { transformEntityToEditModel, transformUpdateModelToEntity } from "../AddIngredientForm/transformModelToEntity";
import { type AddIngredientFormModel } from "../AddIngredientForm/types";
import DishForm from "../DishForm/DishForm";
import { type DishFormModel } from "../DishForm/types";
import { useDishIssues } from "../hooks/useDishIssues";
import { useNutritionMeasures } from "../hooks/useNutritionMeasures";
import { useResolveTraineeIssue, type ResolveTraineeIssuePayload } from "../hooks/useResolveTraineeIssue";
import { useUpdateDishIssue } from "../hooks/useUpdateDishIssue";
import {
  DISH_ISSUE_SOURCE_LABELS,
  getIssueDish,
  getIssueIngredient,
  getIssueName,
  getIssueProductImage,
  getProductDetailsLine,
  getTraineeIssueMetadata,
  getTraineeName,
  INGREDIENT_ISSUE_CATEGORY_LABELS,
  isTraineeDishIssue,
  isTraineeIssue,
  type DishIssueListItem,
} from "./ingredientIssues";
import { TraineeIssueDrawer } from "./TraineeIssueDrawer";

const statusToBadgeStatus: Record<DishIssueStatus, BadgeProps["status"]> = {
  [DishIssueStatus.Reported]: "processing",
  [DishIssueStatus.Resolved]: "success",
  [DishIssueStatus.Closed]: "error",
};

const MAX_SELECTED_ITEMS = 20;

const SOURCE_FILTERS = Object.values(DishIssueSource).map((source) => ({
  text: DISH_ISSUE_SOURCE_LABELS[source],
  value: source,
}));

function DishAIAgentChat({ dishId }: { dishId: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", height: 500 }}>
      <div className="rounded-md border border-dashed border-gray-300 p-3 text-sm text-gray-600">
        Czat AI dla potrawy <strong>{dishId}</strong> jest chwilowo niedostępny w tej wersji klienta.
      </div>
    </div>
  );
}

export const DishIssues = (): React.ReactElement | null => {
  const { issues, isLoading, isFetching, refetch } = useDishIssues({ onlyCount: false });
  const queryClient = useQueryClient();
  const { t } = useTranslation("nutrition");
  const dispatch = useAppDispatch();

  const { showModalForm, toggleModalForm, openFormModal, formModel, editedEntity, hideFormModal } = useFormHandler<
    DishFormModel,
    Dish
  >();
  const {
    isTrainer: isAccountOwner,
    settings: { allowAiAgent: allowAiAgentFeatureFlag },
  } = useUserRole();
  const [loading, onSuccess, onFailure, onStart] = useEntityChange();
  const { measuresMap } = useNutritionMeasures();
  const { createTrainerOptions } = useTeamMembers();
  const [selectedDishIssue, setSelectedDishIssue] = useState<DishIssueListItem | null>(null);
  const { updateIssue, isUpdating } = useUpdateDishIssue();
  const { resolveIssue, isResolving } = useResolveTraineeIssue();
  const [detailsIssueId, setDetailsIssueId] = useState<string | null>(null);
  const [correctedIssue, setCorrectedIssue] = useState<DishIssueListItem | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const focusedIssueId = new URLSearchParams(location.search).get("issueId");
  const refetchedForIssueRef = useRef<string | null>(null);
  const [aiDrawerOpen, setAiDrawerOpen] = useState(false);
  const [aiDrawerDishId] = useState<string | null>(null);
  const { toggleDrawer: toggleBackgroundTasksDrawer } = useBackgroundTasksDrawer();
  const canUseAgents = usePermission(PermissionKey.AGENTS_USE);
  const canApproveIngredients = usePermission(PermissionKey.NUTRITION_INGREDIENTS_APPROVE, PermissionScope.ALL);
  // Filtr „zgłaszający" ma sens dla każdego, kto widzi zgłoszenia spoza swojego
  // przydziału — lustro warunku z useDishIssues.
  const canReadWholeCatalog = usePermission(PermissionKey.NUTRITION_MANAGE, PermissionScope.TEAM);

  // New state for row selection
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<DishIssueListItem[]>([]);

  // Background tasks hook
  const { createDishIssueTasks, isCreatingDishIssueTasks } = useBackgroundTasksQuery({ enabled: false });
  // `settings.allowAiAgent` to przełącznik PER CZŁONEK ZESPOŁU
  // (`team_member.settings`), a ACCOUNT_OWNER ma tam pusty obiekt — nikt mu tego
  // nie ustawia. Gałąź po roli nie obchodzi więc żadnej flagi, tylko domyka lukę
  // „owner nie ma ustawień per członek". Bez niej właściciel konta traci agenta.
  const aiAgentFeatureEnabled = allowAiAgentFeatureFlag || isAccountOwner;
  const allowAiAgent = aiAgentFeatureEnabled && canUseAgents && canApproveIngredients;
  // Row selection configuration
  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[], newSelectedRows: DishIssueListItem[]) => {
      // Limit selection to 30 items
      if (newSelectedRowKeys.length <= MAX_SELECTED_ITEMS) {
        setSelectedRowKeys(newSelectedRowKeys);
        setSelectedRows(newSelectedRows);
      } else {
        // If trying to select more than 30, show a warning
        message.warning("Możesz wybrać maksymalnie 30 elementów");
      }
    },
    getCheckboxProps: (record: DishIssueListItem) => ({
      disabled:
        !record.dishId || (selectedRowKeys.length >= MAX_SELECTED_ITEMS && !selectedRowKeys.includes(record.id)), // Disable checkbox for records without dishId or when limit reached
      name: record.id,
    }),
  };

  // Handle bulk background tasks creation
  const handleCreateBulkBackgroundTasks = async () => {
    const dishIssuesWithDishId = selectedRows.filter((row) => row.dishId);

    if (dishIssuesWithDishId.length === 0) {
      message.warning("Wybierz przynajmniej jedną potrawę do przetworzenia");
      return;
    }

    try {
      await createDishIssueTasks({
        issues: dishIssuesWithDishId.map((row) => ({
          dishId: row.dishId!,
          issueId: row.id,
          reason: row.reason,
          dishTitle: "dish" in row ? row.dish.name : row.ingredient.name,
        })),
      });

      message.success(`Utworzono zadania AI dla ${dishIssuesWithDishId.length} potraw`);
      setSelectedRowKeys([]);
      setSelectedRows([]);
      toggleBackgroundTasksDrawer();

      analyticsService.track("bulk_create_dish_issue_tasks", {
        count: dishIssuesWithDishId.length,
        dishIds: dishIssuesWithDishId.map((row) => row.dishId),
        issueIds: dishIssuesWithDishId.map((row) => row.id),
      });
    } catch (error) {
      message.error("Wystąpił błąd podczas tworzenia zadań AI");
    }
  };

  const onSaveDish = async (
    formData: DishFormModel,
    { portionSize, calories }: { portionSize: number; calories: number },
  ) => {
    const { image, ingredients, updateTemplates, ...rest } = formData;

    if (formModel && editedEntity) {
      const byKey = keyBy(editedEntity.ingredients, "id");
      const dish: Dish = {
        ...editedEntity,
        ...rest,
        media: Array.isArray(image) && image.length ? [{ url: image[0] || "", type: "coverPhoto" }] : null,
        updatedAt: new Date().toISOString(),
        portionSize,
        calories,
        ingredients: formData.ingredients.map((ingredient) => ({
          ...(byKey[ingredient.id] || {}),
          ...ingredient,
          calories: ingredient.nutrients.calories,
          portionCalories: ingredient.nutrients.calories
            ? round((ingredient.nutrients.calories * ingredient.portion) / 100, 1)
            : 0,
        })),
      };

      try {
        onStart();
        await dispatch(nutritionActions.updateDish({ dish, dishId: editedEntity.id })).unwrap();
        if (selectedDishIssue) {
          const resolution = {
            updateTemplates: true,
            flow: "fromDishIssuesTable",
            beforeUpdate: editedEntity,
          };
          // A client's report can only be closed through the RPC; it writes the same metadata, so the
          // webhook rewrites the dish in the templates as it does for a trainer's report.
          if (isTraineeIssue(selectedDishIssue)) {
            const resolved = await resolveTraineeIssue(selectedDishIssue, IngredientIssueOutcome.Resolved, {
              metadata: resolution,
            });
            if (!resolved) {
              onFailure();
              return;
            }
          } else {
            await updateIssue({ status: DishIssueStatus.Resolved, id: selectedDishIssue.id, metadata: resolution });
            await queryClient.invalidateQueries({ queryKey: ["dishIssues"] });
          }
          analyticsService.track("update_dish_in_templates_dish_issues", {
            dishId: editedEntity.id,
            context: "dish_issues",
          });
        }
        onSuccess();
        hideFormModal();
        setSelectedDishIssue(null);
      } catch (e) {
        onFailure();
      }
    }
  };

  const handleEdit = (dish: Dish) => {
    const model: DishFormModel = {
      id: dish.id,
      image: dish.media?.map((media) => media.url) || [],
      tags: dish.tags || [],
      name: dish.name,
      includes: dish.includes || [],
      dishTypes: dish.dishTypes || [],
      kitchenTypes: dish.kitchenTypes || [],
      cookTime: dish.cookTime || null,
      size: dish.size || 2,
      preparationSteps: dish.preparationSteps || [],
      portions: dish.portions || 1,
      needsCooking: dish.needsCooking ?? false,
      needsReheating: dish.needsReheating ?? false,
      mealTypes: dish.mealTypes || [],
      prepTime: dish.prepTime || null,
      ingredients: dish.ingredients.map((ingredient) => {
        const enhancedIngredient = recalculateMeasures(ingredient);
        const { multiplier } = calculateMeasureMultiplier(enhancedIngredient, measuresMap);

        return {
          ...enhancedIngredient,
          multiplier,
        };
      }),
    };
    openFormModal(model, dish);
  };

  // A notification links here with `?issueId=`; that report stays open until the drawer is closed.
  const detailsIssue = issues.find((issue) => issue.id === (detailsIssueId ?? focusedIssueId)) ?? null;

  const closeDetails = () => {
    setDetailsIssueId(null);
    if (focusedIssueId) {
      navigate(location.pathname, { replace: true });
    }
  };

  // A cached list may predate the linked report, so it is refetched once before telling the user it is gone.
  useEffect(() => {
    if (!focusedIssueId) {
      refetchedForIssueRef.current = null;
      return;
    }
    if (isLoading || isFetching || issues.some((issue) => issue.id === focusedIssueId)) {
      return;
    }
    if (refetchedForIssueRef.current !== focusedIssueId) {
      refetchedForIssueRef.current = focusedIssueId;
      void refetch();
      return;
    }
    message.info("To zgłoszenie nie czeka już na decyzję");
    navigate(location.pathname, { replace: true });
  }, [focusedIssueId, isLoading, isFetching, issues, location.pathname, navigate, refetch]);

  /** Resolves `true` once the decision is saved. */
  const resolveTraineeIssue = async (
    issue: DishIssueListItem,
    outcome: IngredientIssueOutcome,
    { changes, metadata }: Pick<ResolveTraineeIssuePayload, "changes" | "metadata"> = {},
  ): Promise<boolean> => {
    // Drop the notification link first, so the refetch after the decision does not read as a missing report.
    if (focusedIssueId) {
      setDetailsIssueId(issue.id);
      navigate(location.pathname, { replace: true });
    }
    try {
      await resolveIssue({ issue, outcome, changes, metadata });
      setDetailsIssueId(null);
      setCorrectedIssue(null);
      return true;
    } catch {
      // The hook already shows the error from the database.
      return false;
    }
  };

  const editIssueDish = (issue: DishIssueListItem) => {
    const dish = getIssueDish(issue);
    if (dish) {
      setSelectedDishIssue(issue);
      handleEdit(dish);
    }
  };

  const correctedIngredient = correctedIssue ? getIssueIngredient(correctedIssue) : null;
  const correctionFormModel = useMemo(
    () => (correctedIngredient ? transformEntityToEditModel(correctedIngredient) : null),
    [correctedIngredient],
  );

  // The trainer edits the product in the regular form, but the write goes through the RPC: only the
  // fields that differ from the current product are sent, as the changes of this report.
  const onSaveCorrectedProduct = async (model: AddIngredientFormModel) => {
    if (!correctedIssue || !correctedIngredient) {
      return;
    }
    const updated = transformUpdateModelToEntity(model, measuresMap, correctedIngredient, false);
    await resolveTraineeIssue(correctedIssue, IngredientIssueOutcome.Resolved, {
      changes: diffIngredientCorrection(correctedIngredient, updated),
    });
  };

  const trainersOptions = createTrainerOptions(true);

  const hasSelected = selectedRowKeys.length > 0;
  const selectedDishCount = selectedRows.filter((row) => row.dishId).length;

  return (
    <Card>
      {allowAiAgent && (
        <div className="mb-4">
          <Space>
            <Button
              type="primary"
              onClick={handleCreateBulkBackgroundTasks}
              disabled={!hasSelected || selectedDishCount === 0 || isCreatingDishIssueTasks}
              loading={isCreatingDishIssueTasks}
            >
              Uruchom zadania Dish Agenta AI ({selectedDishCount})
            </Button>
            <span style={{ marginLeft: 8 }}>{hasSelected ? `Wybrano ${selectedRowKeys.length} elementów` : ""}</span>
          </Space>
        </div>
      )}

      <Table<DishIssueListItem>
        rowSelection={allowAiAgent ? rowSelection : undefined}
        rowKey="id"
        loading={isLoading}
        dataSource={issues}
        scroll={{ x: true }}
        locale={{ emptyText: "Lista potraw do poprawy jest pusta" }}
      >
        <Table.Column<DishIssueListItem>
          title="Nazwa"
          dataIndex="name"
          render={(_, record) => {
            const product = record.dishId ? null : getIssueIngredient(record);
            if (!product) {
              return getIssueName(record);
            }
            const details = getProductDetailsLine(product);
            return (
              <div className="flex items-center gap-3">
                <Avatar
                  shape="square"
                  size={40}
                  src={getIssueProductImage(record) || undefined}
                  icon={<PictureOutlined />}
                />
                <div className="flex flex-col">
                  <span>{getIssueName(record)}</span>
                  {details ? <span className="whitespace-nowrap text-xs text-gray-500">{details}</span> : null}
                </div>
              </div>
            );
          }}
        />
        <Table.Column title="Typ" dataIndex="type" render={(_, record) => (record.dishId ? "Potrawa" : "Produkt")} />
        <Table.Column<DishIssueListItem>
          title="Kategoria"
          dataIndex="categories"
          render={(_, record) =>
            record.categories?.map((category) => (
              <Tag key={category} className="whitespace-nowrap">
                {INGREDIENT_ISSUE_CATEGORY_LABELS[category]}
              </Tag>
            ))
          }
        />
        <Table.Column
          title="Status"
          dataIndex="status"
          render={(status: DishIssueStatus) => (
            <Badge
              status={statusToBadgeStatus[status]}
              text={t(`reportError.status.${status}`)}
              className="whitespace-nowrap"
            />
          )}
        />
        <Table.Column<DishIssueListItem>
          title="Powód"
          dataIndex="reason"
          filters={SOURCE_FILTERS}
          onFilter={(value, record) => record.source === value}
          render={(reason, record) => {
            if (isTraineeIssue(record)) {
              const traineeName = getTraineeName(record.trainee);
              return (
                <Tooltip placement="left" title={traineeName}>
                  <div className="flex items-center gap-2">
                    <Avatar src={record.reporter?.avatarUrl} size={36} icon={<UserOutlined />}>
                      {traineeName ? getUserInitials(traineeName) : null}
                    </Avatar>
                    <div className="flex flex-col">
                      <span className="whitespace-nowrap text-sm">
                        {traineeName} <Tag>{DISH_ISSUE_SOURCE_LABELS[DishIssueSource.Trainee]}</Tag>
                      </span>
                      {reason ? <span className="text-sm text-gray-500">{reason}</span> : null}
                    </div>
                  </div>
                </Tooltip>
              );
            }
            return (
              <Tooltip placement="left" title={record.reporter ? getName(record.reporter) : null}>
                <div className="flex items-center gap-2">
                  <Avatar src={record.reporter?.avatarUrl} size={36}>
                    {record.reporter ? getUserInitials(record.reporter) : null}
                  </Avatar>
                  <span className="whitespace-nowrap text-sm">{reason}</span>
                </div>
              </Tooltip>
            );
          }}
        />
        <Table.Column<DishIssueListItem>
          title="Twórca"
          dataIndex="owner"
          render={(_owner, record) =>
            record?.owner ? (
              <div className="flex items-center gap-2">
                <Avatar src={record.owner.avatarUrl} size={36} icon={<UserOutlined />}>
                  {getUserInitials(record.owner)}
                </Avatar>
                <span className="whitespace-nowrap text-sm">{getName(record.owner)}</span>
              </div>
            ) : (
              "System"
            )
          }
          {...(canReadWholeCatalog
            ? {
                filterSearch: true,
                onFilter: (value, record) =>
                  value === EMPTY_TRAINER_FIELD ? record.ownerId === null : record.ownerId === value,
                filters: trainersOptions,
              }
            : {})}
        />
        <Table.Column
          title="Data zgłoszenia"
          dataIndex="createdAt"
          render={(createdAt) => dayjs(createdAt).format("DD.MM.YYYY HH:mm")}
        />
        <Table.Column<DishIssueListItem>
          title="Opcje"
          dataIndex="actions"
          render={(_, record) =>
            isTraineeIssue(record) ? (
              <Space>
                {isTraineeDishIssue(record) && (
                  <Button type="link" disabled={!getIssueDish(record)} onClick={() => editIssueDish(record)}>
                    Edytuj
                  </Button>
                )}
                {!isTraineeDishIssue(record) && record.status === DishIssueStatus.Reported && (
                  <Button type="link" disabled={!getIssueIngredient(record)} onClick={() => setCorrectedIssue(record)}>
                    Popraw produkt
                  </Button>
                )}
                <Button type="link" onClick={() => setDetailsIssueId(record.id)}>
                  {getTraineeIssueMetadata(record)?.proposedChanges ? "Zobacz propozycję" : "Szczegóły"}
                </Button>
              </Space>
            ) : (
              <Space>
                <Button type="link" onClick={() => editIssueDish(record)}>
                  Edytuj
                </Button>
                {/* {record.dishId && (
                <Button
                  type="link"
                  onClick={() => {
                    setAiDrawerDishId(record.dishId);
                    setAiDrawerOpen(true);
                  }}
                >
                  Użyj agenta AI
                </Button>
              )} */}
              </Space>
            )
          }
        />
      </Table>

      <ModalForm
        open={showModalForm}
        onCancel={() => toggleModalForm(false)}
        title={t("dish.addNewDish")}
        width={900}
        loading={!!loading || isUpdating || isResolving}
      >
        <DishForm onSubmit={onSaveDish} model={formModel} />
      </ModalForm>

      <TraineeIssueDrawer
        issue={detailsIssue}
        loading={isResolving}
        onClose={closeDetails}
        onApplyProposal={(issue) =>
          void resolveTraineeIssue(issue, IngredientIssueOutcome.Resolved, {
            changes: getTraineeIssueMetadata(issue)?.proposedChanges,
          })
        }
        onEditProduct={setCorrectedIssue}
        onEditDish={editIssueDish}
        onResolveWithoutChanges={(issue) => void resolveTraineeIssue(issue, IngredientIssueOutcome.Resolved)}
        onReject={(issue) => void resolveTraineeIssue(issue, IngredientIssueOutcome.Rejected)}
      />

      <ModalForm
        open={Boolean(correctedIssue)}
        onCancel={() => setCorrectedIssue(null)}
        title={t("ingredientForm.editTitle")}
        width={900}
        loading={isResolving}
      >
        <AddIngredientForm model={correctionFormModel} onSubmit={onSaveCorrectedProduct} />
      </ModalForm>

      <Drawer
        title="Agent AI dla potrawy"
        placement="right"
        open={aiDrawerOpen}
        onClose={() => setAiDrawerOpen(false)}
        width={500}
      >
        {aiDrawerDishId && <DishAIAgentChat dishId={aiDrawerDishId} />}
      </Drawer>
    </Card>
  );
};
